import { FetchApiResponse } from '@api/fetchApi';
import { IApiGridResponse, IApiGridSotringParams } from '@api/types';
import type { IDateRange } from '@components-library/Calendar/interfaces';
import type { IInputRange } from '@components-library/Forms/InputNumberRange/InputNumberRange';
import { SortingState } from '@tanstack/react-table';
import {
  getCurrentPlatformDayRangeGivenOffset,
  getCurrentPlatformMonthRangeGivenOffset,
  getCurrentPlatformTimeZone,
  getFormattedServerDateTime,
  getPastPlatformDayRangeGivenOffset,
  getPastPlatformMonthRangeGivenOffset,
  getServerDateTime,
} from '@utils/library/dateTime';
import { isEqual } from 'lodash-es';
import { mobxStore } from 'store/mobx/mobx.store';

class GridFiltersService {
  private mobxAccountStore = mobxStore.accountStore;

  private defaultNumberOfPagesPerRequest = 10;

  serializeRequestData = <T extends Record<string, unknown>>(filtersData: T, sorting: SortingState) => {
    const result = Object.entries(filtersData).reduce<Record<string, unknown>>((acc, [key, value]) => {
      if (this.isDateRange(value)) {
        const dateRange = value as IDateRange;

        acc[key] =
          dateRange.useOffset === false
            ? this.getDateRangeWithoutUtcOffset(dateRange).stringValue
            : this.getDateRangeWithUtcOffset(dateRange).stringValue;
      } else if (this.isInputRange(value)) {
        acc[key] = this.stringifyInputRange(value);
      } else {
        acc[key] = value;
      }
      return acc;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }, {}) as { [key in keyof T]: any } & { orderBy: IApiGridSotringParams[] };

    result.orderBy = sorting.map(({ id, desc }) => ({ column: id, direction: desc ? 'DESC' : 'ASC' }));

    return result;
  };

  sanitizeResponseCheckCountAccuracy = <T>(response: FetchApiResponse<IApiGridResponse<T>>, take: number) => {
    const { data } = response;
    const esitamtedMaxLimit = take * this.defaultNumberOfPagesPerRequest;

    const isCountNotAccurate = data.count - 1 === esitamtedMaxLimit;

    if (!isCountNotAccurate) {
      return response;
    }

    const adjustedCount = Math.min(data.count, esitamtedMaxLimit);

    response.data = {
      ...data,
      count: adjustedCount,
      records: data.records.slice(0, adjustedCount),
      isCountNotAccurate,
    };
    return response;
  };

  isDateRange = (value: unknown): value is IDateRange => {
    if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      if ('startDate' in (value as IDateRange) && 'endDate' in (value as IDateRange)) {
        return true;
      }
    }

    return false;
  };

  isInputRange = (value: unknown): value is IInputRange => {
    if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      if ('from' in (value as IInputRange) && 'to' in (value as IInputRange)) {
        return true;
      }
    }

    return false;
  };

  getDateRangeWithUtcOffset = (value: IDateRange) => {
    const {
      generalSettings: { utcOffsetInHours },
    } = this.mobxAccountStore;

    const start = value.startDate ? getFormattedServerDateTime(value.startDate, utcOffsetInHours) : value.startDate;
    const end = value.endDate ? getFormattedServerDateTime(value.endDate, utcOffsetInHours) : value.endDate;
    const startEndString = `[${start ?? '*'}, ${end ?? '*'}]`;

    return {
      start: value.startDate ? getFormattedServerDateTime(value.startDate, utcOffsetInHours) : value.startDate,
      end: value.endDate ? getFormattedServerDateTime(value.endDate, utcOffsetInHours) : value.endDate,
      stringValue: startEndString,
    };
  };

  getDateRangeWithoutUtcOffset = (value: IDateRange) => {
    const start = value.startDate ? getServerDateTime(value.startDate) : value.startDate;
    const end = value.endDate ? getServerDateTime(value.endDate) : value.endDate;
    const startEndString = `[${start ?? '*'}, ${end ?? '*'}]`;

    return {
      start,
      end,
      stringValue: startEndString,
    };
  };

  stringifyInputRange = (value: IInputRange): string => {
    const format = (value: number | null): string => (value !== null ? String(value) : '*');

    return `[${format(value.from)},${format(value.to)}]`;
  };

  getHasSelectedFilters = (defaultFilters: Record<string, unknown>, currentFilters: Record<string, unknown>) => {
    const reservedNames = ['headerSortValues'];

    const sanitizeEmptyValues = (data: Record<string, unknown>) => {
      return Object.entries(data).reduce<Record<string, unknown>>((acc, [key, value]) => {
        const isEveryKeyEqualToNull = (value: unknown) => value === null;

        if (
          reservedNames.includes(key) ||
          value === null ||
          value === undefined ||
          value === '' ||
          (typeof value === 'object' && Object.values(value).every(isEveryKeyEqualToNull))
        ) {
          return acc;
        } else {
          acc[key] = value;
          return acc;
        }
      }, {} as Record<string, unknown>);
    };

    const defaultSanitizedFilters = sanitizeEmptyValues(defaultFilters);
    const currentSanitizedFilters = sanitizeEmptyValues(currentFilters);

    if (Object.keys(defaultSanitizedFilters).length !== Object.keys(currentSanitizedFilters).length) {
      return true;
    } else {
      return !isEqual(defaultSanitizedFilters, currentSanitizedFilters);
    }
  };

  getPlatformTimeZone = () => {
    const { utcOffsetInHours } = this.mobxAccountStore;

    return getCurrentPlatformTimeZone(utcOffsetInHours);
  };

  getCurrentPlatformDayRange = () => {
    const { utcOffsetInHours } = this.mobxAccountStore;

    return getCurrentPlatformDayRangeGivenOffset(utcOffsetInHours);
  };

  getPastPlatformDayRange = () => {
    const { utcOffsetInHours } = this.mobxAccountStore;

    return getPastPlatformDayRangeGivenOffset(utcOffsetInHours);
  };

  getCurrentPlatformMonthRange = () => {
    const { utcOffsetInHours } = this.mobxAccountStore;

    return getCurrentPlatformMonthRangeGivenOffset(utcOffsetInHours);
  };

  getPastPlatformMonthRange = () => {
    const { utcOffsetInHours } = this.mobxAccountStore;

    return getPastPlatformMonthRangeGivenOffset(utcOffsetInHours);
  };
}

export const gridFiltersService = new GridFiltersService();
